import { useCallback, useRef, useState } from 'react'

import { ExistingFile, GoodieFile, isDirtyFile, isNewFile } from '../types'
import useUploadNewFile from './useUploadFile'
import useUpdateDirtyFile from './useUpdateDirtyFile'

export default function useUpdateFiles(): [
  (files: GoodieFile[]) => Promise<ExistingFile[]>,
  {
    loading: boolean
    progress: number | null
  }
] {
  const [loading, setLoading] = useState(false)
  const [upload] = useUploadNewFile()
  const [updateDirtyFile] = useUpdateDirtyFile()
  const [progress, setProgress] = useState<number | null>(null)
  const progressMap = useRef(new Map<number, number>())

  const onUpdateProgress = useCallback(
    (index: number, progress: number) => {
      progressMap.current.set(index, progress)

      const size = progressMap.current.size

      const sum = progressMap.current
        .values()
        .reduce((sum, curr) => sum + curr, 0)

      setProgress(Math.round(sum / size))
    },
    [setProgress]
  )

  const update = useCallback(
    (files: GoodieFile[]) => {
      setLoading(true)

      const promises = files.map(function (file, index) {
        if (isNewFile(file)) {
          return upload(file, progress => onUpdateProgress(index, progress))
        }

        if (isDirtyFile(file)) return updateDirtyFile(file)

        return Promise.resolve(file)
      })

      return Promise.all(promises).finally(() => {
        setLoading(false)
        setProgress(null)
      })
    },
    [upload, updateDirtyFile, setLoading, onUpdateProgress]
  )

  return [update, { loading, progress }]
}
